import React, { useState, useRef } from "react";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CURRENCY_OPTIONS } from "@/lib/currency";
import { toast } from "sonner";
import { Upload, X, Loader2 } from "lucide-react";

export default function AdminEditRoomDialog({ room, open, onOpenChange, onSaved, apiPath, method = "patch" }) {
    const [form, setForm] = useState(() => ({
        title: room.title || "",
        city: room.city || "",
        country: room.country || "",
        university: room.university || "",
        description: room.description || "",
        rent_per_month: room.rent_per_month || 0,
        currency: room.currency || "EUR",
        available_from: room.available_from || "",
        available_to: room.available_to || "",
        destination_city: room.destination_city || "",
        destination_country: room.destination_country || "",
        amenities: (room.amenities || []).join(", "),
    }));
    const [photos, setPhotos] = useState(room.photos || []);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const removePhoto = (index) => {
        setPhotos((prev) => prev.filter((_, i) => i !== index));
    };

    const handleUploadClick = () => fileInputRef.current?.click();

    const onFileSelected = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        try {
            for (const file of files) {
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await api.post("/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                setPhotos((prev) => [...prev, data.path]);
            }
            toast.success(`${files.length} photo(s) uploaded`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                title: form.title,
                city: form.city,
                country: form.country,
                university: form.university,
                description: form.description,
                rent_per_month: Number(form.rent_per_month) || 0,
                currency: form.currency,
                available_from: form.available_from,
                available_to: form.available_to,
                destination_city: form.destination_city,
                destination_country: form.destination_country,
                amenities: form.amenities
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean),
                photos,
            };
            const endpoint = apiPath || `/admin/rooms/${room.id}`;
            const { data } = method === "put"
                ? await api.put(endpoint, payload)
                : await api.patch(endpoint, payload);
            toast.success("Room updated");
            onSaved && onSaved(data);
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="admin-edit-room-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Edit room</DialogTitle>
                    <DialogDescription>Change any field below. Photos are uploaded to permanent storage.</DialogDescription>
                </DialogHeader>

                <div className="space-y-5 mt-2">
                    {/* Photos */}
                    <div>
                        <Label>Photos ({photos.length})</Label>
                        <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2">
                            {photos.map((p, i) => (
                                <div key={i} className="relative aspect-square rounded-xl overflow-hidden bg-muted border border-border group">
                                    <img loading="lazy" decoding="async" src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                                    <button
                                        type="button"
                                        onClick={() => removePhoto(i)}
                                        data-testid={`edit-remove-photo-${i}`}
                                        className="absolute top-1 right-1 w-7 h-7 rounded-full bg-destructive text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                        title="Remove"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={handleUploadClick}
                                disabled={uploading}
                                data-testid="edit-upload-photo-btn"
                                className="aspect-square rounded-xl border-2 border-dashed border-border hover:border-primary hover:bg-primary/5 transition-colors flex flex-col items-center justify-center text-muted-foreground gap-1"
                            >
                                {uploading ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    <>
                                        <Upload className="w-5 h-5" />
                                        <span className="text-xs">Add photo</span>
                                    </>
                                )}
                            </button>
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={onFileSelected}
                            className="hidden"
                            data-testid="edit-file-input"
                        />
                    </div>

                    <div>
                        <Label htmlFor="ef-title">Title</Label>
                        <Input id="ef-title" value={form.title} onChange={setField("title")} className="mt-1 rounded-xl" data-testid="edit-title-input" />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="ef-city">City</Label>
                            <Input id="ef-city" value={form.city} onChange={setField("city")} className="mt-1 rounded-xl" data-testid="edit-city-input" />
                        </div>
                        <div>
                            <Label htmlFor="ef-country">Country</Label>
                            <Input id="ef-country" value={form.country} onChange={setField("country")} className="mt-1 rounded-xl" data-testid="edit-country-input" />
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="ef-uni">University</Label>
                        <Input id="ef-uni" value={form.university} onChange={setField("university")} className="mt-1 rounded-xl" />
                    </div>

                    <div>
                        <Label htmlFor="ef-desc">Description</Label>
                        <Textarea id="ef-desc" rows={5} value={form.description} onChange={setField("description")} className="mt-1 rounded-xl" data-testid="edit-desc-input" />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                        <div className="sm:col-span-2">
                            <Label htmlFor="ef-rent">Rent / month</Label>
                            <Input id="ef-rent" type="number" value={form.rent_per_month} onChange={setField("rent_per_month")} className="mt-1 rounded-xl" data-testid="edit-rent-input" />
                        </div>
                        <div className="sm:col-span-2">
                            <Label htmlFor="ef-currency">Currency</Label>
                            <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                                <SelectTrigger id="ef-currency" data-testid="edit-currency-select" className="mt-1 rounded-xl">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="max-h-[260px]">
                                    {CURRENCY_OPTIONS.map((c) => (
                                        <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="ef-from">Available from</Label>
                            <Input id="ef-from" type="date" value={form.available_from} onChange={setField("available_from")} className="mt-1 rounded-xl" />
                        </div>
                        <div>
                            <Label htmlFor="ef-to">Available to</Label>
                            <Input id="ef-to" type="date" value={form.available_to} onChange={setField("available_to")} className="mt-1 rounded-xl" />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="ef-dcity">Going to (city)</Label>
                            <Input id="ef-dcity" value={form.destination_city} onChange={setField("destination_city")} className="mt-1 rounded-xl" />
                        </div>
                        <div>
                            <Label htmlFor="ef-dcountry">Going to (country)</Label>
                            <Input id="ef-dcountry" value={form.destination_country} onChange={setField("destination_country")} className="mt-1 rounded-xl" />
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="ef-amen">Amenities (comma separated)</Label>
                        <Input id="ef-amen" value={form.amenities} onChange={setField("amenities")} className="mt-1 rounded-xl" placeholder="wifi, kitchen, heating" />
                    </div>
                </div>

                <DialogFooter className="mt-6">
                    <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="edit-cancel-btn">Cancel</Button>
                    <Button onClick={save} disabled={saving} className="rounded-full bg-primary hover:bg-primary/90" data-testid="edit-save-btn">
                        {saving ? "Saving..." : "Save changes"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
